export const HELP_SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    body: [
      "This page compares station performance between two time periods (Period 1 and Period 2).",
      "Each station is classified into a WDQMS category for the selected metric (availability, timeliness, or quality), and the map shows how many category steps each station moved between the two periods.",
      "Green dots = improvement, blue = no change, orange/red = degradation.",
    ],
  },
  {
    id: "kpis",
    title: "Category Summary KPIs — How to Read the Numbers",
    body: [
      "The KPI bar shows how many stations fall into each WDQMS category in Period 2, along with the change (delta) from Period 1.",
      "",
      "The big number on each card = how many stations are in that category right now (Period 2).",
      "The \u25b2 or \u25bc delta = how many stations moved into (\u25b2) or out of (\u25bc) this category compared to Period 1.",
      "",
      "Example: If 'Normal (\u226580%)' shows 8,500 with \u25b2 +120, it means 120 more stations are in the Normal category in P2 than in P1 \u2014 a positive signal.",
      "Example: If 'Not received' shows 320 with \u25b2 +45, it means 45 more stations stopped reporting \u2014 a negative signal, even though the arrow is up.",
      "",
      "'Total Stations' = all stations in the selected scope (region, country, variable).",
      "'Stopped reporting' = stations that had data in P1 but none in P2. These may have gone offline or been decommissioned.",
      "",
      "What to do: A large \u25b2 in 'Normal' and a large \u25bc in 'Not received' is the ideal outcome. A large \u25b2 in 'Not received' or 'Availability issues' warrants investigation.",
    ],
  },
  {
    id: "map",
    title: "Map Dots \u2014 What the Colors Mean",
    body: [
      "Each dot represents one station, colored by how its WDQMS category changed between P1 and P2.",
      "",
      "Dark green: the station improved by 2+ categories (e.g. from 'Not received' to 'Normal'). This is a strong recovery.",
      "Light green: the station improved by 1 category (e.g. from 'Issues <30%' to 'Issues \u226530%').",
      "Blue: no change \u2014 the station stayed in the same category.",
      "Orange: the station degraded by 1 category (e.g. from 'Normal' to 'Issues \u226530%').",
      "Red: the station degraded by 2+ categories (e.g. from 'Normal' to 'Not received'). This needs urgent attention.",
      "Black: the station had data in P1 but none in P2 \u2014 it stopped reporting.",
      "Purple: the station is new \u2014 it appears in P2 but was not present in P1.",
      "",
      "Click any station dot to see a detailed popup with per-center breakdown, raw values, and an automated cause classification.",
      "The popup also offers a 'View time series' button to see the station's 30-day trend.",
    ],
  },
  {
    id: "legend",
    title: "Legend",
    body: [
      "The legend (bottom-left of the map) explains each dot color. Hover the '?' icon next to each item for more detail.",
    ],
  },
  {
    id: "top-changes",
    title: "Top Changes Panel \u2014 Finding Stations That Need Attention",
    body: [
      "The right panel lists the top 10 stations with the biggest category changes.",
      "",
      "Top Degradations tab: stations that dropped the most categories. These are your priority \u2014 sort by steps to find the worst cases.",
      "Top Improvements tab: stations that gained the most categories. Useful to verify that fixes are working.",
      "",
      "Each row shows: rank, station name, country, P1 category (with color dot), P2 category (with color dot), and the step count.",
      "The step badge shows \u21933 for a 3-category drop or \u21912 for a 2-category gain.",
      "",
      "Click any station to locate it on the map and open its detail popup.",
      "",
      "What to do: Start with the Top Degradations tab. Stations with \u22652 steps degradation (red badge) should be investigated first. Click the station to see the cause classification (e.g. station offline, transmission outage, instrument drift).",
    ],
  },
  {
    id: "station-popup",
    title: "Station Detail Popup \u2014 Understanding a Station's Story",
    body: [
      "When you click a station on the map or in the top changes list, a popup appears with detailed information.",
      "",
      "Per-center breakdown: shows the values from each monitoring centre (DWD, ECMWF, JMA, NCEP) for both periods. If only one centre shows degradation while others are stable, it may be a centre-specific processing issue rather than a station problem.",
      "",
      "Cause classification: an automated rule-based system analyzes the pattern and suggests the most likely cause:",
      "\u2022 Station offline \u2014 the station stopped sending data entirely.",
      "\u2022 GTS transmission outage \u2014 availability dropped but data quality is stable, suggesting a transmission/relay problem.",
      "\u2022 Instrument drift \u2014 quality (O-B departure) worsened and rejections increased, suggesting a sensor or calibration issue.",
      "\u2022 Network connectivity \u2014 timeliness degraded significantly while availability is mostly OK.",
      "\u2022 OSCAR schedule change \u2014 expected count changed but received is stable, indicating a scheduling update in OSCAR.",
      "\u2022 Undetermined \u2014 the pattern doesn't match any known cause clearly.",
      "",
      "Confidence level (High/Medium/Low) indicates how certain the classification is based on the data pattern.",
      "",
      "What to do: Use the cause as a starting point for investigation. 'High' confidence causes are most actionable. For 'Station offline', check the station's operational status. For 'Instrument drift', contact the station operator about calibration. For 'GTS transmission outage', check with the GTS relay/network team.",
    ],
  },
  {
    id: "metrics",
    title: "Metrics Explained \u2014 What Each One Measures",
    body: [
      "Availability: percentage of expected observations actually received.",
      "Categories: Normal (\u226580%), Availability issues (\u226530%), Availability issues (<30%), Not received.",
      "A station at 75% availability means 75 out of 100 expected reports were received.",
      "",
      "Timeliness: average delay in minutes between the observation time and when the report was received.",
      "Categories: <15min, 15\u201330min, 30\u2013120min, >120min.",
      "A station with 45 minutes timeliness means reports arrive, on average, 45 minutes late.",
      "",
      "Quality: absolute background departure (|O-B|) \u2014 the difference between the observation and the NWP model background.",
      "Categories vary by variable (e.g. \u22640.5 hPa, \u22641 hPa, \u22645 hPa, \u226410 hPa, >10 hPa for surface pressure).",
      "A large |O-B| suggests the observation disagrees with the model \u2014 either the observation is biased (instrument issue) or the model has a local error.",
      "",
      "Best across centers: for each station, the best value among DWD, ECMWF, JMA, and NCEP is selected. This means the comparison reflects the most favourable assessment of each station.",
    ],
  },
  {
    id: "workflow",
    title: "Recommended Workflow",
    body: [
      "1. Set your filters \u2014 choose the metric, variable, period type, and dates you want to compare.",
      "2. Check the KPI bar \u2014 are there large deltas? Is 'Not received' growing? Is 'Normal' shrinking?",
      "3. Scan the map \u2014 look for clusters of red/orange dots. These indicate regional issues.",
      "4. Open the Top Degradations tab \u2014 these are your priority stations.",
      "5. Click each degraded station to see the cause classification and per-center breakdown.",
      "6. Use the cause to determine next steps: contact station operator, check GTS relay, review OSCAR schedule, etc.",
      "7. Switch to Top Improvements to verify that previous interventions are working.",
      "8. Use the Export button to download the full comparison as CSV for reporting.",
    ],
  },
  {
    id: "filters",
    title: "Filters",
    body: [
      "Period Type: Daily or Monthly. Monthly data uses the 1st of the selected month.",
      "Period 1 & Period 2: the two dates to compare.",
      "Monitoring Centre: filter by a specific centre or all.",
      "Region / Country: restrict to stations in a WMO region or country.",
      "Variable: the observation variable to compare (e.g. surface pressure, temperature).",
      "Click 'Apply Comparison' to fetch data with the selected filters.",
    ],
  },
];

export const TOUR_STEPS = [
  {
    selector: ".cpp-page-header",
    title: "Temporal Status Comparison",
    body: "Compare station performance between two time periods. Switch metrics here \u2014 availability, timeliness, or quality. Use the '?' button to start this tour anytime.",
  },
  {
    selector: ".csb-root",
    title: "Category Summary KPIs",
    body: "The big number = stations in that category now (P2). The \u25b2\u25bc delta = how many stations moved in/out vs P1. A \u25b2 in 'Normal' is good; a \u25b2 in 'Not received' is bad. Check the Help panel for a detailed guide on reading these numbers.",
  },
  {
    selector: ".cpp-map-area",
    title: "Comparison Map",
    body: "Each dot is a station. Green = improved, red = degraded, blue = no change, black = stopped reporting. Click any dot to see why it changed \u2014 the popup includes an automated cause classification.",
  },
  {
    selector: ".cpp-map-legend",
    title: "Legend",
    body: "Explains each dot color. Hover the '?' icons for more detail on each category movement.",
  },
  {
    selector: ".csp-panel",
    title: "Top Changes \u2014 Your Action List",
    body: "Start here: the Top Degradations tab lists stations that dropped the most. Click a station to see the cause (offline, transmission, instrument drift, etc.) and take action.",
  },
  {
    selector: ".cfp-root",
    title: "Filters",
    body: "Set your period type, dates, centre, region, and variable here. Click 'Apply Comparison' to update the results.",
  },
];
